import React from 'react';
import { Link } from 'react-router-dom';
import Container from '../../components/layout/Container/Container';
import cvData from '../../assets/data/cv-data';
import './Home.css';
import Blog from '../Blog/Blog.js';

const truncateText = (text = '', limit = 180) => {
  if (!text) return '';
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}...`;
};

const Home = () => {
  const featuredProjects = cvData.projects.filter((project) => project.featured).slice(0, 3);
  const homeServices = cvData.services || [];
  const topSkills = [
    ...cvData.skills.backend.slice(0, 4),
    ...cvData.skills.frontend.slice(0, 5),
    ...cvData.skills.tools.slice(0, 4)
  ];
  const capabilityGroups = [
    {
      title: 'Frontend',
      icon: 'fas fa-palette',
      description: 'Responsive interfaces with React, Angular, Bootstrap, and modern UI patterns.',
      skills: cvData.skills.frontend.slice(0, 8)
    },
    {
      title: 'Backend',
      icon: 'fas fa-database',
      description: 'Practical application logic, APIs, and database-driven systems for real projects.',
      skills: cvData.skills.backend.slice(0, 8)
    },
    {
      title: 'Tools',
      icon: 'fas fa-cog',
      description: 'Delivery workflows with Git, cloud platforms, databases, and deployment tooling.',
      skills: cvData.skills.tools.slice(0, 8)
    }
  ];
  return (
    <div className="home-page">
      {/* Hero Section - Modern Gradient */}
      <section className="home-hero">
        <Container>
          <div className="hero-grid">
            <div className="hero-content">
              <div className="hero-badge">
                <span className="hero-badge-pulse"></span>
                Software Developer & Instructor
              </div>
              <h1 className="hero-title">
                Building <span className="hero-title-accent">practical software</span> with clarity and purpose
              </h1>
              <p className="hero-description">
                {cvData.personalInfo.bio}
              </p>
              <div className="hero-actions">
                <Link to="/contact" className="btn-primary-modern">
                  <span>Hire Me</span>
                  <i className="fas fa-arrow-right"></i>
                </Link>
                <Link to="/projects" className="btn-outline-modern">
                  <i className="fas fa-code-branch"></i>
                  <span>Explore Projects</span>
                </Link>
              </div>
              <div className="hero-skills">
                {topSkills.map((skill) => (
                  <span key={skill.name} className="hero-skill">
                    {skill.name}
                  </span>
                ))}
              </div>
            </div>
            <div className="hero-visual">
              <div className="hero-visual-ring"></div>
              <div className="hero-visual-ring hero-visual-ring-2"></div>
              <div className="hero-photo-wrapper">
                <img
                  src={cvData.personalInfo.photo}
                  alt={cvData.personalInfo.name}
                  className="hero-photo"
                />
                <div className="hero-photo-glow"></div>
              </div>
              <div className="hero-stats">
                <div className="hero-stat">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{cvData.personalInfo.location}</span>
                </div>
                <div className="hero-stat">
                  <i className="fas fa-briefcase"></i>
                  <span>{cvData.personalInfo.experienceYears} Years Experience</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Stats Section - Clean Numbers */}
      <section className="stats-section">
        <Container>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-number">{cvData.personalInfo.experienceYears}</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">7+</span>
              <span className="stat-label">Projects Delivered</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">100+</span>
              <span className="stat-label">Students Mentored</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">100%</span>
              <span className="stat-label">Client Satisfaction</span>
            </div>
          </div>
        </Container>
      </section>

      {/* Skills, Blog, Projects - Three Column Layout */}
      <section className="features-section">
        <Container>
          <div className="section-header">
            <span className="section-tag">What I Do</span>
            <h2 className="section-title-modern">Skills, insights & <span className="gradient-text">featured work</span></h2>
            <p className="section-subtitle">A complete overview of my expertise, latest thinking, and highlighted projects</p>
          </div>

          <div className="three-col-grid">
            {/* Skills Column */}
            <div className="feature-card feature-card-skills">
              <div className="feature-card-header">
                <div className="feature-icon">
                  <i className="fas fa-code"></i>
                </div>
                <div>
                  <span className="feature-label">Expertise</span>
                  <h3 className="feature-title">Technical Skills</h3>
                </div>
              </div>
              <div className="skills-stack">
                {capabilityGroups.map((group) => (
                  <div key={group.title} className="skill-group-modern">
                    <div className="skill-group-header">
                      <i className={group.icon}></i>
                      <div>
                        <h4>{group.title}</h4>
                        <p>{group.description}</p>
                      </div>
                    </div>
                    <div className="skill-chips">
                      {group.skills.map((skill) => (
                        <div key={skill.name} className="skill-chip-modern">
                          <span>{skill.name}</span>
                          <div className="skill-bar">
                            <div className="skill-bar-fill" style={{ width: `${skill.level}%` }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Blog Column */}
            <div className="feature-card feature-card-blog">
              <div className="feature-card-header">
                <div className="feature-icon">
                  <i className="fas fa-pen-fancy"></i>
                </div>
                <div>
                  <span className="feature-label">Blog</span>
                  <h3 className="feature-title">Latest Articles</h3>
                </div>
              </div>
              <div className="home-blog-preview">
                <Blog />
              </div>
              <Link to="/blog" className="view-all-link blog-view-more-link">
                View More Articles <i className="fas fa-arrow-right"></i>
              </Link>
            </div>

            {/* Projects Column */}
            <div className="feature-card feature-card-projects">
              <div className="feature-card-header">
                <div className="feature-icon">
                  <i className="fas fa-rocket"></i>
                </div>
                <div>
                  <span className="feature-label">Portfolio</span>
                  <h3 className="feature-title">Featured Projects</h3>
                </div>
              </div>
              <div className="projects-list">
                {featuredProjects.map((project) => (
                  <div key={project.id} className="project-card-modern">
                    <div className="project-image">
                      <img src={project.image} alt={project.title} />
                      <div className="project-overlay">
                        <Link to={`/projects/${project.id}`} className="project-link">
                          View Project
                        </Link>
                      </div>
                    </div>
                    <div className="project-info">
                      <div className="project-tech">
                        {project.technologies.slice(0, 2).map((tech) => (
                          <span key={tech}>{tech}</span>
                        ))}
                      </div>
                      <h4>{project.title}</h4>
                      <p>{truncateText(project.description, 80)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/projects" className="view-all-link">
                View All Projects <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* Focus Areas - Service Cards */}
      <section className="services-section">
        <Container>
          <div className="section-header centered">
            <span className="section-tag">How I Help</span>
            <h2 className="section-title-modern">Areas of <span className="gradient-text">expertise</span></h2>
            <p className="section-subtitle">Specialized skills that deliver real results for teams and learners</p>
          </div>
          <div className="services-grid">
            {homeServices.map((service) => (
              <div key={service.id} className="service-card">
                <div className="service-icon">
                  <i className={service.icon}></i>
                </div>
                <div className="service-meter">
                  <div className="service-meter-label">
                    <span>Capability</span>
                    <strong>{service.proficiency}%</strong>
                  </div>
                  <div className="service-meter-track">
                    <div className="service-meter-fill" style={{ width: `${service.proficiency}%` }}></div>
                  </div>
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <div className="service-tags">
                  {service.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Section - Bold Statement */}
      <section className="cta-section-modern">
        <Container>
          <div className="cta-wrapper">
            <div className="cta-content">
              <span className="cta-badge">Let's Work Together</span>
              <h2>Ready to bring your <span className="gradient-text">ideas to life</span>?</h2>
              <p>I'm open to new opportunities, practical software work, and meaningful teaching-focused projects.</p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn-primary-modern btn-large">
                  <span>Start a Conversation</span>
                  <i className="fas fa-comment-dots"></i>
                </Link>
                <Link to="/blog" className="btn-outline-modern btn-large">
                  <i className="fas fa-book-open"></i>
                  <span>Read My Blog</span>
                </Link>
              </div>
            </div>
            <div className="cta-pattern"></div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Home;
